// Captions on upload (Peter 2026-11-02: "all photos uploaded and processed by
// QuoteMaker should be captioned ... it should get captioned on upload").
//
// Once a project photo is stored (photo-persistence.js
// uploadPhotoToS3IfPossible), it is queued here. The server writes the caption
// from the stored photo alone (backend routes/trusted-images.js
// POST /api/media/photo-caption); this announces it as PHOTO_CAPTION_EVENT,
// and App.tsx puts it on that photo's `caption` when the photo belongs to the
// open project and has none. The save path stores it like any photo field,
// every path that puts a photo on a quote item copies the photo, caption
// included, and Stage 5 prints and edits it.
//
// Runs beside the import, never in front of it: an upload does not wait for
// its caption. Three calls at a time. A caption that arrives after the builder
// has left the job is dropped; the Quote Brain still captions the photos its
// quote carries when its run finishes (backend quote-brain-job/photo-captions.js).

import { apiFetch } from './data-service-api.js';

export const PHOTO_CAPTION_EVENT = 'qm:photo-caption';

export interface PhotoCaptionDetail {
    projectId: string;
    photoId: string;
    s3Key: string;
    caption: string;
}

type CaptionJob = Omit<PhotoCaptionDetail, 'caption'>;

const CONCURRENCY = 3;
const queue: CaptionJob[] = [];
const queued = new Set<string>();
let running = 0;

/** Queues a caption for a stored photo of the project that has none. */
export function queuePhotoCaption(photo: any, projectId: unknown): void {
    if (!photo || typeof photo !== 'object' || typeof photo.caption === 'string') return;
    const photoId = typeof photo.id === 'string' ? photo.id : '';
    const s3Key = typeof photo.s3Key === 'string' ? photo.s3Key : '';
    const project = typeof projectId === 'string' || typeof projectId === 'number' ? String(projectId) : '';
    if (!photoId || !s3Key || !project) return;
    const key = `${project}\n${photoId}\n${s3Key}`;
    if (queued.has(key)) return;
    queued.add(key);
    queue.push({ projectId: project, photoId, s3Key });
    pump();
}

function pump(): void {
    while (running < CONCURRENCY && queue.length) {
        const job = queue.shift()!;
        running += 1;
        void captionOne(job).finally(() => {
            running -= 1;
            pump();
        });
    }
}

async function captionOne(job: CaptionJob): Promise<void> {
    try {
        // The call writes nothing, so a dropped connection may repeat it.
        const result: any = await apiFetch(`/media/photo-caption?projectId=${encodeURIComponent(job.projectId)}`, {
            method: 'POST',
            body: JSON.stringify({ s3Key: job.s3Key }),
            retry: { idempotent: true, attempts: 3 },
        });
        const caption = typeof result?.caption === 'string' ? result.caption.trim() : '';
        if (!caption || typeof window === 'undefined') return;
        window.dispatchEvent(new CustomEvent<PhotoCaptionDetail>(PHOTO_CAPTION_EVENT, { detail: { ...job, caption } }));
    } catch {
        // No caption: Stage 5 asks the builder for one, as before.
    }
}

/** The photos with the caption on the photo it names when that photo has none; the same array when nothing changes. */
export function withPhotoCaption<T>(photos: T[], detail: Pick<PhotoCaptionDetail, 'photoId' | 'caption'>): T[] {
    if (!Array.isArray(photos) || !detail?.photoId || !detail.caption) return photos;
    let changed = false;
    const next = photos.map((p: any) => {
        if (!p || typeof p !== 'object' || p.id !== detail.photoId || typeof p.caption === 'string') return p;
        changed = true;
        return { ...p, caption: detail.caption };
    });
    return changed ? next : photos;
}

/** For tests. */
export function __resetPhotoCaptionQueue(): void {
    queue.length = 0;
    queued.clear();
    running = 0;
}
